// ─────────────────────────────────────────────
// Oike Acompañamiento — time zones and booking slots. Pure functions, no I/O.
// Used by the server (coaching-admin / coaching-portal compute and re-check the
// slots) and by the screens (to show times in each person's time zone).
// Dates are stored in UTC (ISO); availability is written in the coach's zone.
// ─────────────────────────────────────────────

export function isValidTimeZone(tz) {
  if (typeof tz !== 'string' || !tz || tz.length > 64) return false;
  try { new Intl.DateTimeFormat('en-US', { timeZone: tz }); return true; } catch { return false; }
}

/** Offset (minutes) of a time zone at one instant: local = utc + offset. */
export function tzOffsetMinutes(date, tz) {
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', {
    timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(date).filter(p => p.type !== 'literal').map(p => [p.type, Number(p.value)]));
  const asUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour % 24, parts.minute, parts.second);
  return Math.round((asUtc - Math.floor(date.getTime() / 1000) * 1000) / 60000);
}

/** A wall-clock date and time in a time zone → the UTC instant. */
export function zonedToUtc(day, time, tz) {
  const [y, m, d] = day.split('-').map(Number);
  const [hh, mm] = time.split(':').map(Number);
  const guess = Date.UTC(y, m - 1, d, hh, mm);
  let ts = guess - tzOffsetMinutes(new Date(guess), tz) * 60000;
  // Second pass for the days the offset changes
  ts = guess - tzOffsetMinutes(new Date(ts), tz) * 60000;
  return new Date(ts);
}

/** YYYY-MM-DD and weekday (0 = Sunday) of an instant in a time zone. */
export function localDay(date, tz) {
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit', weekday: 'short' })
    .formatToParts(date).filter(p => p.type !== 'literal').map(p => [p.type, p.value]));
  const weekday = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(parts.weekday);
  return { day: `${parts.year}-${parts.month}-${parts.day}`, weekday };
}

export function addDaysIso(day, n) {
  const d = new Date(`${day}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

export const overlaps = (a, b) => new Date(a.start).getTime() < new Date(b.end).getTime() && new Date(b.start).getTime() < new Date(a.end).getTime();

const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

/** The availability as the coach saved it, cleaned (anything invalid is dropped). */
export function cleanAvailability(v, defaults) {
  const out = { ...defaults, weekly: {} };
  const src = v && typeof v === 'object' ? v : {};
  if (isValidTimeZone(src.timeZone)) out.timeZone = src.timeZone;
  for (const wd of [0, 1, 2, 3, 4, 5, 6]) {
    const ranges = Array.isArray(src.weekly?.[wd]) ? src.weekly[wd] : [];
    out.weekly[wd] = ranges
      .filter(r => Array.isArray(r) && TIME_RE.test(r[0]) && TIME_RE.test(r[1]) && r[0] < r[1])
      .slice(0, 6).map(r => [r[0], r[1]]);
  }
  out.blocked = (Array.isArray(src.blocked) ? src.blocked : []).filter(d => typeof d === 'string' && DAY_RE.test(d)).slice(0, 200);
  const num = (k, min, max) => { const n = Number(src[k]); if (Number.isFinite(n) && n >= min && n <= max) out[k] = Math.round(n); };
  num('sessionMinutes', 15, 240); num('bufferMinutes', 0, 120); num('minNoticeHours', 0, 24 * 14);
  num('horizonDays', 1, 120); num('slotStepMinutes', 15, 120);
  return out;
}

/**
 * Free slots: the weekly windows in the coach's zone, minus blocked days, minus
 * anything that overlaps a busy session (with the buffer on both sides), from
 * now + minimum notice to the horizon.
 */
export function availableSlots(av, busy, { now = new Date(), durationMinutes } = {}) {
  const duration = durationMinutes || av.sessionMinutes;
  const buffer = (av.bufferMinutes || 0) * 60000;
  const earliest = now.getTime() + (av.minNoticeHours || 0) * 3600000;
  const busyRanges = (busy || []).map(b => ({ start: new Date(new Date(b.start).getTime() - buffer).toISOString(), end: new Date(new Date(b.end).getTime() + buffer).toISOString() }));
  const startDay = localDay(now, av.timeZone).day;
  const out = [];
  for (let i = 0; i <= av.horizonDays; i++) {
    const day = addDaysIso(startDay, i);
    if (av.blocked.includes(day)) continue;
    const weekday = new Date(`${day}T12:00:00Z`).getUTCDay();
    for (const [from, to] of av.weekly[weekday] || []) {
      const windowEnd = zonedToUtc(day, to, av.timeZone).getTime();
      for (let t = zonedToUtc(day, from, av.timeZone).getTime(); t + duration * 60000 <= windowEnd; t += av.slotStepMinutes * 60000) {
        if (t < earliest) continue;
        const slot = { start: new Date(t).toISOString(), end: new Date(t + duration * 60000).toISOString() };
        if (!busyRanges.some(b => overlaps(slot, b))) out.push(slot);
      }
    }
  }
  return out;
}

/** True if this exact slot is still free (the server checks it again before saving a booking). */
export function slotIsAvailable(av, busy, start, { now = new Date(), durationMinutes } = {}) {
  return availableSlots(av, busy, { now, durationMinutes }).some(s => s.start === new Date(start).toISOString());
}

export function fmtInTz(iso, tz, opts = { dateStyle: 'full', timeStyle: 'short' }) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('es', { ...opts, timeZone: isValidTimeZone(tz) ? tz : undefined }); } catch { return new Date(iso).toLocaleString('es'); }
}

/** An .ics file for one session (download only; no external calendar involved). */
export function icsFor({ uid, start, end, title, description = '' }) {
  const stamp = (iso) => new Date(iso).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  const esc = (s) => String(s).replace(/[\\,;]/g, m => `\\${m}`).replace(/\n/g, '\\n');
  return ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Oike//Acompanamiento//ES', 'BEGIN:VEVENT',
    `UID:${uid}@oike`, `DTSTAMP:${stamp(new Date().toISOString())}`, `DTSTART:${stamp(start)}`, `DTEND:${stamp(end)}`,
    `SUMMARY:${esc(title)}`, `DESCRIPTION:${esc(description)}`, 'END:VEVENT', 'END:VCALENDAR'].join('\r\n');
}
